import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { regionMasterAPI, masterDataAPI, territoryMasterAPI } from "../services/api";
import Swal from "sweetalert2";
import $ from "jquery";
import "select2";
import "select2/dist/css/select2.min.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "./RaiseComplaint.css";
import "./AddEditRegionMaster.css";
import { getRoutePermissions } from "../utils/auth";

const normalizeCountry = (item) => ({
  countryCode: item.countryCode || item.CountryCode || item.COUNTRYCODE || "",
  countryName: item.countryName || item.CountryName || item.COUNTRYNAME || "",
});

const normalizeRegion = (item) => ({
  regionCode: item.regionCode || item.RegionCode || item.REGIONCODE || "",
  regionId: item.regionId || item.RegionId || item.REGIONID || "",
  regionName: item.regionName || item.RegionName || item.REGIONNAME || "",
  countryCode: item.countryCode || item.CountryCode || item.COUNTRYCODE || "",
  zoneCode: item.zoneCode || item.ZoneCode || item.ZONECODE || "",
  isActive: item.isActive ?? item.IsActive ?? true,
});

const normalizeZone = (item) => ({
  zoneCode: item.zoneCode || item.ZoneCode || item.ZONECODE || "",
  zoneId: item.zoneId || item.ZoneId || item.ZONEID || "",
  zoneName: item.zoneName || item.ZoneName || item.ZONENAME || "",
  countryCode: item.countryCode || item.CountryCode || item.COUNTRYCODE || "",
});

const AddEditRegionMaster = () => {
  const navigate = useNavigate();
  const { regionCode } = useParams();
  const isEditMode = !!regionCode;

  const routePermissions = getRoutePermissions("/regions");
  const { canAdd, canEdit } = routePermissions;
  const canSave = isEditMode ? canEdit : canAdd;

  const [region, setRegion] = useState({
    regionCode: "",
    regionId: "",
    regionName: "",
    countryCode: "",
    zoneCode: "",
    isActive: true,
  });

  const [dropdowns, setDropdowns] = useState({
    countries: [],
    zones: [],
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const countrySelectRef = useRef(null);
  const zoneSelectRef = useRef(null);

  const currentUser = (() => {
    try {
      const u = localStorage.getItem("user");
      return u ? JSON.parse(u) : null;
    } catch (e) {
      return null;
    }
  })();

  useEffect(() => {
    if (!canSave) {
      Swal.fire({
        title: "Access Denied",
        text: `You do not have permission to ${isEditMode ? "edit" : "add"} regions.`,
        icon: "error",
        confirmButtonText: "OK",
      }).then(() => navigate("/regions"));
      return;
    }
    loadDropdowns();
    if (isEditMode) {
      loadRegion();
    } else {
      loadNextRegionCode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regionCode, canSave]);

  // Initialize Select2 for Country dropdown
  useEffect(() => {
    if (dropdowns.countries.length === 0) {
      return;
    }

    if (countrySelectRef.current) {
      const $countrySelect = $(countrySelectRef.current);
      if ($countrySelect.data("select2")) {
        $countrySelect.select2("destroy");
      }
      $countrySelect.select2({
        width: "100%",
        placeholder: "Select Country",
        allowClear: false,
      });
      $countrySelect.on("select2:select", function (e) {
        const selectedCountryCode = e.params.data.id;
        handleChange("countryCode", selectedCountryCode);
        handleChange("zoneCode", ""); // Reset zone when country changes
        loadZones(selectedCountryCode);
      });
      if (region.countryCode) {
        $countrySelect.val(region.countryCode).trigger("change");
      }
    }

    return () => {
      if (countrySelectRef.current) {
        const $select = $(countrySelectRef.current);
        if ($select.data("select2")) {
          $select.off("select2:select");
          $select.select2("destroy");
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropdowns.countries]);

  // Initialize Select2 for Zone dropdown
  useEffect(() => {
    if (dropdowns.zones.length === 0) {
      return;
    }

    if (zoneSelectRef.current) {
      const $zoneSelect = $(zoneSelectRef.current);
      if ($zoneSelect.data("select2")) {
        $zoneSelect.select2("destroy");
      }
      $zoneSelect.select2({
        width: "100%",
        placeholder: "Select Zone",
        allowClear: false,
      });
      $zoneSelect.on("select2:select", function (e) {
        const selectedZoneCode = e.params.data.id;
        handleChange("zoneCode", selectedZoneCode);
      });
      if (region.zoneCode) {
        $zoneSelect.val(region.zoneCode).trigger("change");
      }
    }

    return () => {
      if (zoneSelectRef.current) {
        const $select = $(zoneSelectRef.current);
        if ($select.data("select2")) {
          $select.off("select2:select");
          $select.select2("destroy");
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropdowns.zones]);

  // Update Select2 value when region changes
  useEffect(() => {
    if (countrySelectRef.current && dropdowns.countries.length > 0) {
      const $countrySelect = $(countrySelectRef.current);
      if ($countrySelect.data("select2")) {
        const currentVal = $countrySelect.val();
        if (currentVal !== (region.countryCode || "")) {
          $countrySelect.val(region.countryCode || "").trigger("change");
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [region.countryCode]);

  // Update Select2 value when zone changes
  useEffect(() => {
    if (zoneSelectRef.current && dropdowns.zones.length > 0) {
      const $zoneSelect = $(zoneSelectRef.current);
      if ($zoneSelect.data("select2")) {
        const currentVal = $zoneSelect.val();
        if (currentVal !== (region.zoneCode || "")) {
          $zoneSelect.val(region.zoneCode || "").trigger("change");
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [region.zoneCode]);

  const loadDropdowns = async () => {
    try {
      setLoading(true);
      const countryRes = await masterDataAPI.getCountries();

      setDropdowns({
        countries: (countryRes?.data || []).map((c) => normalizeCountry(c)),
        zones: [],
      });
    } catch (e) {
      console.error("Error loading dropdowns:", e);
      setError("Failed to load dropdown data. Please refresh the page.");
    } finally {
      setLoading(false);
    }
  };

  const loadZones = async (countryCode) => {
    if (!countryCode) {
      setDropdowns((prev) => ({ ...prev, zones: [] }));
      return;
    }
    try {
      const response = await territoryMasterAPI.getZones(countryCode);
      if (response?.success && response.data) {
        const items = response.data.zones || response.data.Zones || response.data || [];
        const activeZones = Array.isArray(items)
          ? items.filter((z) => z.isActive !== false && z.IsActive !== false)
          : [];
        setDropdowns((prev) => ({
          ...prev,
          zones: activeZones.map((z) => ({
            zoneCode: z.zoneCode || z.ZoneCode || "",
            zoneName: z.zoneName || z.ZoneName || "",
            countryCode: z.countryCode || z.CountryCode || countryCode,
          })),
        }));
      }
    } catch (e) {
      console.error("Error loading zones:", e);
    }
  };

  const loadNextRegionCode = async () => {
    try {
      const response = await regionMasterAPI.getNextCode();
      if (response && response.success && response.data) {
        setRegion((prev) => ({ ...prev, regionCode: response.data }));
      }
    } catch (e) {
      console.error("Error loading next region code:", e);
      setRegion((prev) => ({ ...prev, regionCode: "RG00001" }));
    }
  };

  const loadRegion = async () => {
    try {
      setLoading(true);
      const response = await regionMasterAPI.getByCode(regionCode);
      if (response && response.success && response.data) {
        const regionData = normalizeRegion(response.data);
        setRegion(regionData);
        if (regionData.countryCode) {
          await loadZones(regionData.countryCode);
        }
      } else {
        navigate("/regions");
      }
    } catch (e) {
      console.error("Error loading region:", e);
      if (e.response?.status !== 404) {
        Swal.fire("Error", "Failed to load region details", "error");
      }
      navigate("/regions");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setRegion((prev) => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    if (!region.regionCode?.trim()) {
      Swal.fire("Error", "Region Code is required", "error");
      return false;
    }
    if (!region.regionName?.trim()) {
      Swal.fire("Error", "Region Name is required", "error");
      return false;
    }
    if (!region.countryCode) {
      Swal.fire("Error", "Country is required", "error");
      return false;
    }
    if (!region.zoneCode) {
      Swal.fire("Error", "Zone is required", "error");
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      const createdBy = currentUser?.userIdCode || "SYSADMIN001";
      let response;

      if (isEditMode) {
        response = await regionMasterAPI.update(
          regionCode,
          region,
          createdBy
        );
      } else {
        response = await regionMasterAPI.create(region, createdBy);
      }

      if (response && response.success) {
        Swal.fire({
          title: "Success",
          text: response.message || "Region saved successfully",
          icon: "success",
          confirmButtonText: "OK",
        }).then(() => {
          navigate("/regions");
        });
      } else {
        Swal.fire(
          "Error",
          response.message || "Failed to save region",
          "error"
        );
      }
    } catch (e) {
      console.error("Error saving region:", e);
      Swal.fire(
        "Error",
        e.response?.data?.message ||
          "Failed to save region. Please try again.",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box className="add-edit-product-loading">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box className="add-edit-product-header">
        <Breadcrumbs
          aria-label="breadcrumb"
          className="add-edit-product-breadcrumb"
        >
          <Link
            underline="hover"
            color="inherit"
            href="/regions"
            onClick={(e) => {
              e.preventDefault();
              navigate("/regions");
            }}
            className="breadcrumb-link brdcrmbLstitms"
          >
            Region Master
          </Link>
          <Typography
            color="text.primary"
            className="breadcrumb-current brdcrmbLstitms brdcrmbCurPg"
          >
            {isEditMode ? "Edit Region" : "Add Region"}
          </Typography>
        </Breadcrumbs>
        <Box className="add-edit-product-header-right">
          <Button
            className="add-edit-product-back-button"
            onClick={() => navigate("/regions")}
            startIcon={<ArrowBackIcon />}
          >
            Back
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert
          severity="error"
          className="add-edit-product-error-alert"
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      <div className="container-fluid add-edit-product-page">
        {/* Basic Information Section */}
        <div className="card-panel mb-3">
          <div className="row g-3 mb-4">
            <div className="col-12 col-md-6 col-lg-4" style={{ display: "none" }}>
              <div className="textfield-wrapper">
                <label className="select2-label pnlLblsze">
                  Region Code <span style={{ color: "red" }}>*</span>
                </label>
                <TextField
                  value={region.regionCode}
                  required
                  fullWidth
                  disabled={true}
                  size="small"
                  className="add-edit-product-textfield"
                />
              </div>
            </div>
            <div className="col-12 col-md-6 col-lg-4">
              <div className="textfield-wrapper">
                <label className="select2-label pnlLblsze">
                  Region ID
                </label>
                <TextField
                  value={region.regionId}
                  onChange={(e) => handleChange("regionId", e.target.value)}
                  fullWidth
                  size="small"
                  placeholder="Enter region ID"
                  className="add-edit-product-textfield"
                />
              </div>
            </div>
            <div className="col-12 col-md-6 col-lg-4">
              <div className="select2-wrapper">
                <label className="select2-label pnlLblsze">
                  Country <span style={{ color: "red" }}>*</span>
                </label>
                <select
                  ref={countrySelectRef}
                  className="form-control select2-country"
                  required
                >
                  <option value="">Select Country</option>
                  {dropdowns.countries.map((c) => (
                    <option key={c.countryCode} value={c.countryCode}>
                      {c.countryName}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="col-12 col-md-6 col-lg-4">
              <div className="select2-wrapper">
                <label className="select2-label pnlLblsze">
                  Zone <span style={{ color: "red" }}>*</span>
                </label>
                <select
                  ref={zoneSelectRef}
                  className="form-control select2-zone"
                  required
                  disabled={!region.countryCode}
                >
                  <option value="">Select Zone</option>
                  {dropdowns.zones.map((z) => (
                    <option key={z.zoneCode} value={z.zoneCode}>
                      {z.zoneName}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="col-12 col-md-6 col-lg-4">
              <div className="textfield-wrapper">
                <label className="select2-label pnlLblsze">
                  Region Name <span style={{ color: "red" }}>*</span>
                </label>
                <TextField
                  value={region.regionName}
                  onChange={(e) => handleChange("regionName", e.target.value)}
                  required
                  fullWidth
                  size="small"
                  placeholder="Enter region name"
                  className="add-edit-product-textfield"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="row mt-4">
          <div className="col-12">
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="contained"
                onClick={() => navigate("/regions")}
                className="btn-cancel-product funbtns"
                startIcon={<CancelIcon />}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving || !canSave}
                startIcon={
                  saving ? <CircularProgress size={20} /> : <SaveIcon />
                }
                className="btn-save-product funbtns"
              >
                {saving ? "Saving..." : "Save"}
              </Button>
            </Stack>
          </div>
        </div>
      </div>
    </Box>
  );
};

export default AddEditRegionMaster;



